<template>
  <div class="page-outer hall-form-page">
    <div class="page-header page-header-fixed-form">
      <div class="header-left header-left--inline">
        <h1 class="page-title">
          <i class="ri-layout-column-line"></i>
          <template v-if="isEdit">編輯館別<span v-if="originalName">：{{ originalName }}</span></template>
          <template v-else>新增館別</template>
        </h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" :disabled="saving || loading || !!loadError" @click="goBack">取消</button>
        <button type="button" class="btn btn-primary" :disabled="saving || loading || !!loadError" @click="saveHall">
          <span v-if="saving" class="spinner-sm"></span>
          {{ saving ? '儲存中...' : (isEdit ? '儲存變更' : '建立館別') }}
        </button>
      </div>
    </div>

    <div class="page-container">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>

      <div v-else-if="loadError" class="error-state">
        <i class="ri-error-warning-line"></i> {{ loadError }}
        <button type="button" class="btn btn-secondary btn-sm" @click="loadHall">重新載入</button>
      </div>

      <div v-else class="form-container">
        <div class="form-section">
          <h3 class="section-title">基本資訊</h3>
          <div class="form-group">
            <label class="form-label required">館別名稱</label>
            <input v-model="form.name" class="form-control" placeholder="例：周年慶特價館" />
          </div>
          <div class="form-group">
            <label class="form-label">URL Slug</label>
            <input v-model="form.slug" class="form-control" placeholder="留空則儲存時依館別名稱自動產生" />
            <FormTipBox>
              <p class="form-hint"><strong>用途：</strong>作為前台「館別頁」的網址代碼（路徑中的一段）。同一商店內不可與其他館別重複；公開 API 亦以同一段 slug 查詢館別（<code>.../public/commerce/halls/:slug</code>）。</p>
              <p class="form-hint"><strong>留空時：</strong>儲存後由系統依「館別名稱」自動產生：空白與常見分隔符會合併為 <code>-</code>、會移除不適合作為路徑的字元（如 <code>? # / \\ %</code>）。若與既有館別衝突，會自動改為 <code>slug-2</code>、<code>slug-3</code>… 直到唯一。</p>
              <p class="form-hint"><strong>自行填寫：</strong>建議僅使用中文、英數字與連字號 <code>-</code>；拉丁字母會轉成小寫。</p>
              <p class="form-hint form-hint--preview">
                <strong>前台路徑預覽：</strong>
                <code class="slug-preview-code">/halls/{{ previewHallSlug }}</code>
                <span class="form-hint-sub">完整網址為「商店前台網域」+ 上列路徑（下方為依目前輸入推估，與儲存後若遇衝突微調之結果可能略有差異）。</span>
              </p>
            </FormTipBox>
          </div>
          <div class="form-group">
            <label class="form-label">封面圖片</label>
            <ImageUploader v-model="form.coverImageUrl" :uploadFn="uploadImage" shape="card" hint="建議尺寸 16:9 / 1200x675px" />
          </div>
          <div class="form-group">
            <label class="form-label">描述</label>
            <textarea v-model="form.description" class="form-control" rows="3" placeholder="館別說明文字（可選）"></textarea>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">排序</label>
              <input v-model.number="form.sortOrder" type="number" min="0" class="form-control" />
            </div>
            <div class="form-group form-group--status">
              <span class="form-label">上下架</span>
              <div class="hall-active-toggle">
                <span class="hall-active-label" :class="{ 'hall-active-label--on': form.isActive }">
                  {{ form.isActive ? '上架' : '下架' }}
                </span>
                <label class="switch switch--hall">
                  <input v-model="form.isActive" type="checkbox" />
                  <span class="slider"></span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="form-section">
          <CommerceSeoMetaFields
            v-model:seo-title="form.seoTitle"
            v-model:seo-description="form.seoDescription"
            v-model:seo-keywords="form.seoKeywords"
            variant="hall"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { usePageTitle } from '@/composables/usePageTitle.js'
import { commerceSettingsApi } from '@/api/modules/commerceSettings.js'
import { showError, showSuccess } from '@/utils/swal.js'
import ImageUploader from '@/components/ImageUploader.vue'
import FormTipBox from '@/components/form/FormTipBox.vue'
import CommerceSeoMetaFields from '@/components/commerce/CommerceSeoMetaFields.vue'
import apiClient from '@/api/axios.js'

const route = useRoute()
const router = useRouter()

const isEdit = computed(() => route.name === 'ClientCommerceHallEdit')

/**
 * 與後端 commerceStorefrontService.slugify 一致，供預覽；實際儲存仍以前端送出的 body 與後端去重為準。
 */
function slugifyPreview(text) {
  if (!text || typeof text !== 'string') return ''
  return text
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[?#/\\%]/g, '')
    .replace(/[\s\-–—_]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .trim()
}

/** 前台館別頁路徑片段預覽（對應公開 API 之 :slug） */
const previewHallSlug = computed(() => {
  const manual = form.value.slug?.trim()
  if (manual) {
    const s = slugifyPreview(manual)
    return s || '（請調整 Slug 內容）'
  }
  const name = form.value.name?.trim()
  if (name) {
    const s = slugifyPreview(name)
    return s || '（請手動填寫 Slug）'
  }
  return '（請先填寫館別名稱）'
})

usePageTitle(isEdit.value ? '編輯館別' : '新增館別')

const loading = ref(false)
const loadError = ref('')
const saving = ref(false)
const originalHallId = ref('')
const originalName = ref('')

const form = ref({
  name: '',
  slug: '',
  coverImageUrl: '',
  description: '',
  sortOrder: 0,
  isActive: true,
  seoTitle: '',
  seoDescription: '',
  seoKeywords: '',
})

function goBack() {
  router.push('/client/commerce/halls')
}

function initEmptyForm() {
  form.value = {
    name: '',
    slug: '',
    coverImageUrl: '',
    description: '',
    sortOrder: 0,
    isActive: true,
    seoTitle: '',
    seoDescription: '',
    seoKeywords: '',
  }
}

async function loadHall() {
  if (!isEdit.value) {
    initEmptyForm()
    return
  }
  const id = route.params.hallId
  if (!id) {
    loadError.value = '缺少館別 ID'
    return
  }
  loading.value = true
  loadError.value = ''
  try {
    const res = await commerceSettingsApi.getHall(id)
    const hall = res?.data ?? {}
    originalHallId.value = hall.id ?? id
    originalName.value = hall.name ?? ''
    form.value = {
      name: hall.name ?? '',
      slug: hall.slug ?? '',
      coverImageUrl: hall.cover_image_url ?? '',
      description: hall.description ?? '',
      sortOrder: hall.sort_order ?? 0,
      isActive: hall.is_active !== false,
      seoTitle: hall.seo_title ?? hall.seoTitle ?? '',
      seoDescription: hall.seo_description ?? hall.seoDescription ?? '',
      seoKeywords: hall.seo_keywords ?? hall.seoKeywords ?? '',
    }
  } catch (e) {
    loadError.value = e?.response?.data?.message || '載入失敗'
  } finally {
    loading.value = false
  }
}

async function uploadImage(file) {
  const fd = new FormData()
  fd.append('file', file)
  return apiClient.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
}

async function saveHall() {
  if (!form.value.name?.trim()) {
    await showError('請填寫館別名稱')
    return
  }
  saving.value = true
  try {
    if (isEdit.value) {
      if (!originalHallId.value) {
        await showError('找不到館別識別')
        return
      }
      await commerceSettingsApi.updateHall(originalHallId.value, form.value)
      await showSuccess('館別更新成功')
    } else {
      await commerceSettingsApi.createHall(form.value)
      await showSuccess('館別建立成功')
    }
    router.push('/client/commerce/halls')
  } catch (e) {
    await showError(e?.response?.data?.message || '儲存失敗')
  } finally {
    saving.value = false
  }
}

onMounted(loadHall)
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  margin-bottom: 0;
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.header-left--inline {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.header-actions { display: flex; align-items: center; gap: 8px; }

.page-header .page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.page-container {
  padding: 20px;
  padding-top: 72px;
}

.btn { padding: 6px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; border: none; display: inline-flex; align-items: center; gap: 6px; }
.btn-primary { background: #3b82f6; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #fff; color: #374151; border: 1px solid #d1d5db; }
.btn-secondary:hover:not(:disabled) { background: #f9fafb; }
.btn-secondary:disabled { opacity: 0.6; cursor: not-allowed; }

.spinner-sm {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: hall-form-spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes hall-form-spin {
  to { transform: rotate(360deg); }
}

.loading-state,
.error-state {
  background: #fff;
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  color: #6b7280;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
}
.error-state { color: #b91c1c; }
.error-state .btn { margin-left: 10px; }

.form-container {
  max-width: 900px;
  margin: 0 auto;
  background: #fff;
  border-radius: 12px;
  padding: 28px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.form-section { margin-bottom: 32px; }
.form-section:last-child { margin-bottom: 0; }

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #222;
  margin: 0 0 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #f0f0f0;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.form-group { display: flex; flex-direction: column; margin-bottom: 16px; }
.form-group:last-child { margin-bottom: 0; }
.form-group--status {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}
.hall-active-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
  min-height: 44px;
  box-sizing: border-box;
}
.hall-active-label {
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
}
.hall-active-label--on {
  color: #166534;
}
.switch {
  position: relative;
  display: inline-block;
  width: 46px;
  height: 24px;
  flex-shrink: 0;
}
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.switch .slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #d1d5db;
  border-radius: 24px;
  transition: background 0.2s;
}
.switch .slider:before {
  position: absolute;
  content: '';
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background: white;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s;
}
.switch--hall input:checked + .slider {
  background: #3b82f6;
}
.switch--hall input:checked + .slider:before {
  transform: translateX(22px);
}
.switch--hall input:focus-visible + .slider {
  outline: 2px solid #93c5fd;
  outline-offset: 2px;
}
.form-label { font-size: 13px; font-weight: 500; color: #555; margin-bottom: 5px; }
.form-label.required::after { content: ' *'; color: #e74c3c; }

.form-control {
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
}
.form-control:focus {
  outline: none;
  border-color: #1a73e8;
  box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.15);
}

@media (max-width: 768px) {
  .page-container { padding: 12px; padding-top: 72px; }
  .form-container { padding: 16px; }
  .form-row { grid-template-columns: 1fr; }
}
</style>
